import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, IconButton, Tooltip, Typography } from "@mui/material";
import FolderIcon from "@mui/icons-material/Folder";
import DeleteIcon from "@mui/icons-material/Delete";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import NotesIcon from "@mui/icons-material/Notes";
import SummarizeIcon from "@mui/icons-material/Summarize";
import DriveFileMoveIcon from "@mui/icons-material/DriveFileMove";
import WebAssetIcon from "@mui/icons-material/WebAsset";
import { useParams } from "react-router-dom";

import type { RootState } from "../store/state/rootReducer";
import type { AppDispatch } from "../store/state/rootStore";
import { clearSelectedStories } from "../store/state/stories/actions";
import { useRoleFlags } from "../hooks/usePermissions";
import { AddToFoldersModal } from "../Pages/Project/Modals/AddToFoldersModal";
import {
  addBulkStoryToFolders,
  loadProjectStories,
  moveBulkStoriesToProject,
  resetStoriesProject,
  setProjectStoriesPageView,
} from "../store/actions/projects";
import { getFolder, getProjectFolders, resetFolderState } from "../store/actions/folders";
import { loadPortals } from "../store/actions/portals";
import { BulkDeleteModal } from "../Pages/Project/Modals/BulkDeleteModal";
import useMixPanel from "../lib/mixpanel";
import { BulkDownloadModal } from "../Pages/Project/Modals/BulkDownloadModal";
import { AutoIndexModalModal } from "../Pages/Project/Modals/AutoIndexModal";
import { GPTSummaryModal } from "../Pages/Project/Modals/GPTSummaryModal";
import { getEligibleAIStoryIds } from "../Pages/Project/Modals/aiGenerationUtils";
import { MoveToProjectModal } from "../Pages/Project/Modals/MoveToProjectModal";
import { AddStoryToPortalModal } from "../Pages/Portals/AddStoryToPortalModal";

export const FooterActionBar = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { project_id: projectId, folder_id: folderId } = useParams();
  const { isAdmin, isOrgManager, isProjectManager } = useRoleFlags();
  const selectedStories = useSelector((state: RootState) => state.stories.selectedStories);
  const selectedStoriesIds = selectedStories.map((story) => story._id);
  const projectFolders = useSelector((state: RootState) => state.folders?.projectFolders) ?? [];

  const [isBulkAddToFolderModalOpen, setIsBulkAddToFolderModalOpen] = useState(false);
  const [isDeleteStoriesModalOpen, setIsDeleteStoriesModalOpen] = useState(false);
  const [isDownloadStoriesModalOpen, setIsDownloadStoriesModalOpen] = useState(false);
  const [isBulkIndexModalOpen, setIsBulkIndexModalOpen] = useState(false);
  const [isBulkSummaryModalOpen, setIsBulkSummaryModalOpen] = useState(false);
  const [isMoveToProjectModalOpen, setIsMoveToProjectModalOpen] = useState(false);
  const [isAddToPortalModalOpen, setIsAddToPortalModalOpen] = useState(false);

  const hasSelectedStories = !!selectedStoriesIds?.length;
  const hasFolders = !!projectFolders?.length;
  const hasPermissionForBulkActions = isProjectManager || isAdmin;
  const canMoveStoriesToProject = (isOrgManager || isAdmin) && !!projectId;
  const userFlags = useSelector((state: RootState) => state.featureFlags?.flags);
  const portals = useSelector((state: RootState) => state.portals?.portals) ?? [];
  const isLoadingPortals = useSelector((state: RootState) => state.portals?.isLoadingPortals);
  const canAddToPortal = Boolean(userFlags?.enablePortals) && portals.some((portal) => portal.collections.length > 0);
  const eligibleAIStoryIds = getEligibleAIStoryIds(selectedStories);
  const hasEligibleAIStories = eligibleAIStoryIds.length > 0;
  const requestedPortalsRef = useRef(false);

  useEffect(() => {
    if (!userFlags?.enablePortals || isLoadingPortals || portals.length > 0 || requestedPortalsRef.current) return;

    requestedPortalsRef.current = true;
    dispatch(loadPortals());
  }, [dispatch, isLoadingPortals, portals.length, userFlags?.enablePortals]);

  const onConfirmBulkAddToFolderHandler = async (selectedFoldersIds: string[]) => {
    useMixPanel("bulk_add_to_folder_stories", { stories: selectedStoriesIds });
    await dispatch(addBulkStoryToFolders(selectedStoriesIds, selectedFoldersIds));
    const isFolder = !!(folderId && projectId);

    if (isFolder) {
      dispatch(resetFolderState());
      dispatch(getFolder(folderId));
    } else {
      dispatch(resetStoriesProject());
      dispatch(setProjectStoriesPageView({ currentPage: 1 }));
      dispatch(loadProjectStories({ projectId }));
      dispatch(getProjectFolders(projectId));
    }
    dispatch(clearSelectedStories());
    setIsBulkAddToFolderModalOpen(false);
  };

  const onConfirmMoveToProjectHandler = async (targetProjectId: string) => {
    if (!projectId) {
      return;
    }

    useMixPanel("bulk_move_to_project", {
      stories: selectedStoriesIds,
      source_project_id: projectId,
      target_project_id: targetProjectId,
    });

    const didMove = await dispatch(moveBulkStoriesToProject(projectId, targetProjectId, selectedStoriesIds));
    if (!didMove) {
      return;
    }

    const isFolder = !!(folderId && projectId);

    if (isFolder) {
      dispatch(resetFolderState());
      dispatch(getFolder(folderId));
    } else {
      dispatch(resetStoriesProject());
      dispatch(setProjectStoriesPageView({ currentPage: 1 }));
      dispatch(loadProjectStories({ projectId }));
      dispatch(getProjectFolders(projectId));
    }

    dispatch(clearSelectedStories());
    setIsMoveToProjectModalOpen(false);
  };

  if (!hasSelectedStories) {
    return null;
  }

  return (
    <>
      <Box
        display="flex"
        position="absolute"
        bottom={16}
        right={16}
        zIndex={1200}
        bgcolor={"#fff"}
        padding="8px 16px"
        borderRadius={2}
        id="footer-action-bar"
        alignItems="center"
        width="calc(100% - 216px)"
        justifyContent="space-between"
        sx={{ backgroundColor: "#464C53" }}
      >
        <Box>
          <Typography fontWeight={700} fontSize="14px" color="#FFFFFF">
            {selectedStoriesIds.length} {selectedStoriesIds.length > 1 ? "interviews" : "interview"} selected
          </Typography>
        </Box>
        <Box display="flex" columnGap={1}>
          {userFlags?.enableSpeechmaticsAutochaptering && hasPermissionForBulkActions && (
            <Tooltip
              title={
                hasEligibleAIStories ? "Generate indexes" : "Selected interviews need completed transcript processing"
              }
              placement="top"
            >
              <Box component="span" display="flex">
                <IconButton onClick={() => setIsBulkIndexModalOpen(true)} disabled={!hasEligibleAIStories} size="small">
                  <NotesIcon style={{ color: "#FFFFFF", width: "20px", height: "20px" }} />
                </IconButton>
              </Box>
            </Tooltip>
          )}
          {userFlags?.enableChatGpt && hasPermissionForBulkActions && (
            <Tooltip
              title={
                hasEligibleAIStories ? "Generate summaries" : "Selected interviews need completed transcript processing"
              }
              placement="top"
            >
              <Box component="span" display="flex">
                <IconButton
                  onClick={() => setIsBulkSummaryModalOpen(true)}
                  disabled={!hasEligibleAIStories}
                  size="small"
                >
                  <SummarizeIcon style={{ color: "#FFFFFF", width: "20px", height: "20px" }} />
                </IconButton>
              </Box>
            </Tooltip>
          )}
          {canMoveStoriesToProject && (
            <Tooltip title="Move to project" placement="top">
              <IconButton size="small" onClick={() => setIsMoveToProjectModalOpen(true)}>
                <DriveFileMoveIcon style={{ color: "#FFFFFF", width: "20px", height: "20px" }} />
              </IconButton>
            </Tooltip>
          )}
          {hasFolders && hasPermissionForBulkActions && (
            <Tooltip title="Manage folder(s)" placement="top">
              <IconButton size="small" onClick={() => setIsBulkAddToFolderModalOpen(true)}>
                <FolderIcon style={{ color: "#FFFFFF", width: "20px", height: "20px" }} />
              </IconButton>
            </Tooltip>
          )}
          {canAddToPortal && (
            <Tooltip title="Add to portal" placement="top">
              <IconButton
                size="small"
                onClick={() => {
                  dispatch(loadPortals());
                  setIsAddToPortalModalOpen(true);
                }}
              >
                <WebAssetIcon style={{ color: "#FFFFFF", width: "20px", height: "20px" }} />
              </IconButton>
            </Tooltip>
          )}
          {hasPermissionForBulkActions && (
            <Tooltip title="Delete" placement="top">
              <IconButton size="small" onClick={() => setIsDeleteStoriesModalOpen(true)}>
                <DeleteIcon style={{ color: "#FFFFFF", width: "20px", height: "20px" }} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Download" placement="top">
            <IconButton size="small" onClick={() => setIsDownloadStoriesModalOpen(true)}>
              <FileDownloadIcon style={{ color: "#FFFFFF", width: "20px", height: "20px" }} />
            </IconButton>
          </Tooltip>
          <Button
            onClick={() => dispatch(clearSelectedStories())}
            sx={{ textTransform: "none", color: "#FFFFFF", fontSize: "14px", fontWeight: 600 }}
            variant="text"
          >
            Cancel
          </Button>
        </Box>
      </Box>
      {isBulkAddToFolderModalOpen && (
        <AddToFoldersModal
          showModal={isBulkAddToFolderModalOpen}
          onCancel={() => setIsBulkAddToFolderModalOpen(false)}
          onConfirm={onConfirmBulkAddToFolderHandler}
        />
      )}
      {isDeleteStoriesModalOpen && (
        <BulkDeleteModal showModal={isDeleteStoriesModalOpen} onCancel={() => setIsDeleteStoriesModalOpen(false)} />
      )}
      {isMoveToProjectModalOpen && (
        <MoveToProjectModal
          showModal={isMoveToProjectModalOpen}
          onCancel={() => setIsMoveToProjectModalOpen(false)}
          onConfirm={onConfirmMoveToProjectHandler}
        />
      )}
      {isDownloadStoriesModalOpen && (
        <BulkDownloadModal
          showModal={isDownloadStoriesModalOpen}
          onCancel={() => {
            setIsDownloadStoriesModalOpen(false);
          }}
        />
      )}
      {isBulkIndexModalOpen && (
        <AutoIndexModalModal
          showModal={isBulkIndexModalOpen}
          onCancel={() => setIsBulkIndexModalOpen(false)}
          storyIds={selectedStoriesIds}
          stories={selectedStories}
          onComplete={() => dispatch(clearSelectedStories())}
        />
      )}
      {isBulkSummaryModalOpen && (
        <GPTSummaryModal
          showModal={isBulkSummaryModalOpen}
          onCancel={() => setIsBulkSummaryModalOpen(false)}
          storyIds={selectedStoriesIds}
          stories={selectedStories}
          onComplete={() => dispatch(clearSelectedStories())}
        />
      )}
      {isAddToPortalModalOpen && (
        <AddStoryToPortalModal
          open={isAddToPortalModalOpen}
          storyIds={selectedStoriesIds}
          onClose={() => setIsAddToPortalModalOpen(false)}
        />
      )}
    </>
  );
};
